import { useMemo, useRef, useState } from "react";

import { SuccessToast, useSuccessFeedback } from "./useSuccessFeedback";
import CameraIcon from "./common/CameraIcon";
import EditIcon from "./common/EditIcon";
import ListTextPreview from "./common/ListTextPreview";
import TrashIcon from "./common/TrashIcon";
import { createAttachmentName } from "../utils/attachmentNaming";
import { processImageFile } from "../utils/imageProcessing";

const effortTypes = [
  "Umpacken",
  "Sortieren",
  "Etikettieren",
  "Pruefen",
  "Displaybau",
  "Grossauftrag",
  "Schadensprotokoll",
  "VAS sonstiges",
  "Auslagen",
];

const expenseTypes = [
  "Abstimmung mit Kunde",
  "Etikettierung",
  "Fotodokumentation",
  "Paletten Defekt/ nicht getauscht/ entsorgt",
  "Pruefung/ Bestandspruefung",
  "Retourenbearbeitung",
  "Schadensprotokoll",
  "Sortierung",
  "Sonstige Zusatzleistung",
  "Umpacken von Ware",
  "Verauslagte Kosten",
];

const initialForm = {
  customer: "",
  effortType: "",
  customEffort: "",
  comment: "",
  durationMinutes: 10,
  expenseType: "",
  expenseAmount: "",
  displayCount: 1,
  displayUnitPrice: 14,
  damageReportCount: 1,
  damageReportUnitPrice: 25,
  damagedPackages: 0,
  damagedPallets: 0,
  photoData: "",
  photoDataList: [],
};

const durationOptions = Array.from({ length: 72 }, (_, index) => (index + 1) * 10);

function getEmployeeName(user) {
  return user?.username || "";
}

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(seconds / 60);
  const restSeconds = seconds % 60;

  return `${minutes}m ${String(restSeconds).padStart(2, "0")}s`;
}

function formatEuro(value) {
  const amount = Number(value) || 0;

  return amount > 0
    ? amount.toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR",
      })
    : "-";
}

function getStoredImages(item, listFieldName, legacyFieldName) {
  if (Array.isArray(item?.[listFieldName]) && item[listFieldName].length > 0) {
    return item[listFieldName];
  }

  return item?.[legacyFieldName] ? [item[legacyFieldName]] : [];
}

function getImageDownloadName(effort, dataUrl, index) {
  return createAttachmentName({
    area: "Mehraufwand",
    processNumber: effort.processNumber,
    attachmentType: "BILD",
    sequence: index + 1,
    dataUrl,
  });
}

function downloadCsv(fileName, rows) {
  const csvContent = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(";")
    )
    .join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function Mehraufwand({
  efforts,
  suppliers,
  user,
  onSaveEffort,
  onDeleteEffort,
  onToggleProcessed,
  onUpdateBilling,
}) {
  const [form, setForm] = useState(initialForm);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [costSummaryFilter, setCostSummaryFilter] = useState("open");
  const [timeSummaryFilter, setTimeSummaryFilter] = useState("open");
  const [timeRate, setTimeRate] = useState("7.50");
  const [effortsVisible, setEffortsVisible] = useState(false);
  const [processStartedAt, setProcessStartedAt] = useState(null);
  const photoInputRef = useRef(null);
  const { successVisible, triggerSuccessFeedback } = useSuccessFeedback();
  const suppliersByName = useMemo(() => {
    return new Map(suppliers.map((supplier) => [supplier.name, supplier]));
  }, [suppliers]);
  const employeeName = getEmployeeName(user);

  const employees = useMemo(() => {
    return Array.from(new Set(efforts.map((effort) => effort.employee).filter(Boolean)));
  }, [efforts]);

  const filteredEfforts = useMemo(() => {
    return [...efforts].reverse().filter((effort) => {
      const matchesCustomer =
        filterCustomer === "all" || effort.customer === filterCustomer;
      const matchesType = filterType === "all" || effort.effortType === filterType;
      const matchesEmployee =
        filterEmployee === "all" || effort.employee === filterEmployee;
      const query = searchTerm.trim().toLowerCase();
      const effortLabel =
        effort.effortType === "VAS sonstiges" && effort.customEffort
          ? `${effort.effortType} ${effort.customEffort}`
          : effort.effortType;
      const expenseLabel = String(effort.expenseType || "");
      const matchesSearch =
        query.length === 0 ||
        effort.customer.toLowerCase().includes(query) ||
        String(suppliersByName.get(effort.customer)?.customerNumber || "")
          .toLowerCase()
          .includes(query) ||
        effortLabel.toLowerCase().includes(query) ||
        expenseLabel.toLowerCase().includes(query) ||
        String(effort.comment || "").toLowerCase().includes(query) ||
        String(effort.employee || "").toLowerCase().includes(query);

      return matchesCustomer && matchesType && matchesEmployee && matchesSearch;
    });
  }, [efforts, filterCustomer, filterEmployee, filterType, searchTerm, suppliersByName]);

  const matchesProcessedFilter = (effort, summaryFilter) =>
    summaryFilter === "all" ||
    (summaryFilter === "open" && !effort.processed) ||
    (summaryFilter === "processed" && effort.processed);

  const costSummary = useMemo(() => {
    const expenseEfforts = filteredEfforts.filter(
      (effort) =>
        effort.effortType === "Auslagen" &&
        matchesProcessedFilter(effort, costSummaryFilter)
    );
    return expenseEfforts.reduce(
      (sum, effort) => sum + (Number(effort.expenseAmount) || 0),
      0
    );
  }, [costSummaryFilter, filteredEfforts]);

  const timeSummary = useMemo(() => {
    const durationEfforts = filteredEfforts.filter(
      (effort) =>
        Number(effort.durationMinutes) > 0 &&
        matchesProcessedFilter(effort, timeSummaryFilter)
    );
    const minutes = durationEfforts.reduce(
      (sum, effort) => sum + (Number(effort.durationMinutes) || 0),
      0
    );
    const rate = Number(String(timeRate).replace(",", ".")) || 0;
    return {
      minutes,
      amount: Math.ceil(minutes / 10) * rate,
    };
  }, [filteredEfforts, timeRate, timeSummaryFilter]);

  const handleSave = async () => {
    if (!form.customer) {
      window.alert("Bitte waehle zuerst einen Kunden aus.");
      return;
    }

    if (!form.effortType) {
      window.alert("Bitte waehle die Art des Aufwandes aus.");
      return;
    }

    if (form.effortType === "Auslagen" && (Number(form.expenseAmount) || 0) <= 0) {
      window.alert("Bitte gib den Wert der Auslagen in Euro ein.");
      return;
    }

    if (form.effortType === "Auslagen" && !form.expenseType) {
      window.alert("Bitte waehle die Auslagenart aus.");
      return;
    }

    if (
      form.effortType === "Displaybau" &&
      ((Number(form.displayCount) || 0) < 1 ||
        (Number(form.displayCount) || 0) > 10000)
    ) {
      window.alert("Bitte gib eine Display-Anzahl von 1 bis 10000 ein.");
      return;
    }

    if (
      form.effortType === "Schadensprotokoll" &&
      ((Number(form.damageReportCount) || 0) < 1 ||
        (Number(form.damageReportCount) || 0) > 10000)
    ) {
      window.alert("Bitte gib eine Schadensprotokoll-Anzahl von 1 bis 10000 ein.");
      return;
    }

    try {
      await onSaveEffort({
        timestamp: new Date().toLocaleString("de-DE"),
        customer: form.customer,
        effortType: form.effortType,
        customEffort: "",
        comment: form.comment.trim(),
        durationMinutes:
          form.effortType === "Auslagen" ? 0 : Number(form.durationMinutes),
        expenseType: form.effortType === "Auslagen" ? form.expenseType : "",
        expenseAmount: Number(form.expenseAmount) || 0,
        displayCount: Number(form.displayCount) || 1,
        displayUnitPrice: Number(form.displayUnitPrice) || 14,
        damageReportCount: Number(form.damageReportCount) || 1,
        damageReportUnitPrice: Number(form.damageReportUnitPrice) || 25,
        processDurationSeconds: processStartedAt
          ? Math.max(0, Math.round((Date.now() - processStartedAt) / 1000))
          : 0,
        employee: employeeName,
        photoData: form.photoData,
        photoDataList: form.photoDataList,
      });

      setForm(initialForm);
      setProcessStartedAt(null);
      triggerSuccessFeedback();
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleDelete = async (effortId) => {
    try {
      await onDeleteEffort(effortId);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleToggleProcessed = async (effort) => {
    try {
      await onToggleProcessed(effort.id, !effort.processed);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleEditExpenseAmount = async (effort) => {
    const rawValue = window.prompt(
      "Wert in Euro eingeben",
      String(Number(effort.expenseAmount) || 0)
    );

    if (rawValue === null) {
      return;
    }

    try {
      await onUpdateBilling(effort.id, {
        durationMinutes: Number(effort.durationMinutes) || 0,
        expenseAmount: Number(String(rawValue).replace(",", ".")) || 0,
      });
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleEditDuration = async (effort) => {
    const rawValue = window.prompt(
      "Dauer in Minuten eingeben",
      String(Number(effort.durationMinutes) || 0)
    );

    if (rawValue === null) {
      return;
    }

    try {
      await onUpdateBilling(effort.id, {
        durationMinutes: Number(rawValue) || 0,
        expenseAmount: Number(effort.expenseAmount) || 0,
      });
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleImageCapture = async (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    try {
      const dataUrls = await Promise.all(files.map(processImageFile));
      setForm((currentForm) => {
        const photoDataList = [...currentForm.photoDataList, ...dataUrls];

        return {
          ...currentForm,
          photoDataList,
          photoData: photoDataList[0] || "",
        };
      });
    } catch (error) {
      window.alert(error.message);
    } finally {
      event.target.value = "";
    }
  };

  const openStoredImage = (dataUrl) => {
    const imageWindow = window.open();

    if (!imageWindow) {
      window.alert("Bitte erlaube Pop-ups, damit das Bild geoeffnet werden kann.");
      return;
    }

    imageWindow.document.write(
      `<img src="${dataUrl}" alt="Gespeichertes Bild" style="max-width:100%;height:auto;display:block;margin:0 auto;" />`
    );
    imageWindow.document.close();
  };

  const downloadStoredImage = (dataUrl, fileName) => {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterCustomer("all");
    setFilterType("all");
    setFilterEmployee("all");
  };

  const handleCsvDownload = () => {
    if (filteredEfforts.length === 0) {
      window.alert("Es gibt keine Daten fuer die aktuellen Filter.");
      return;
    }

    const headers = [
      "Zeit",
      "Kunde",
      "Kundennummer",
      "Aufwand",
      "Auslagen",
      "Auslagenart",
      "Wert EUR",
      "Beschädigte Pakete",
      "Beschädigte Paletten",
      "Kommentar",
      "Zeit in Minuten",
      "Bearbeitungszeit",
      "Mitarbeiter",
      "Foto",
      "Bearbeitet",
    ];
    const rows = filteredEfforts.map((effort) => {
      const supplier = suppliersByName.get(effort.customer);

      return [
        effort.timestamp,
        effort.customer,
        supplier?.customerNumber || "",
        effort.effortType === "VAS sonstiges" && effort.customEffort
          ? `${effort.effortType}: ${effort.customEffort}`
          : effort.effortType,
        effort.effortType === "Auslagen" ? "Ja" : "Nein",
        effort.expenseType || "",
        Number(effort.expenseAmount) || 0,
        Number(effort.damagedPackages) || 0,
        Number(effort.damagedPallets) || 0,
        effort.comment,
        effort.effortType === "Auslagen" ? "" : effort.durationMinutes,
        formatDuration(effort.processDurationSeconds),
        effort.employee,
        getStoredImages(effort, "photoDataList", "photoData").length > 0
          ? "Ja"
          : "Nein",
        effort.processed ? "Ja" : "Nein",
      ];
    });
    const safeCustomer =
      filterCustomer === "all"
        ? "alle-kunden"
        : filterCustomer.toLowerCase().replace(/[^a-z0-9]+/gi, "-");
    const safeType =
      filterType === "all"
        ? "alle-aufwaende"
        : filterType.toLowerCase().replace(/[^a-z0-9]+/gi, "-");

    downloadCsv(`mehraufwand-${safeCustomer}-${safeType}.csv`, [
      headers,
      ...rows,
    ]);
  };

  return (
    <div className="page-stack">
      <SuccessToast visible={successVisible} />
      <div className="mehraufwand-head">
        <div>
          <p className="eyebrow">Zusatzaufwand</p>
          <h1 className="page-title">Mehraufwand</h1>
          <p className="section-text">
            Erfasse Zusatzarbeiten mit Kunde, Aufwand, Zeit, Mitarbeiter und optionalem Foto.
          </p>
        </div>
        {user?.isAdmin && (
        <div className="mehraufwand-summary-row">
          <div className="mehraufwand-summary-card">
            <h2>Kosten</h2>
            <label className="field">
              <select
                value={costSummaryFilter}
                onChange={(event) => setCostSummaryFilter(event.target.value)}
              >
                <option value="all">Wert EUR gesamt</option>
                <option value="open">Wert EUR nicht bearbeitet</option>
                <option value="processed">Wert EUR bearbeitet</option>
              </select>
            </label>
            <strong>{formatEuro(costSummary)}</strong>
          </div>
          <div className="mehraufwand-summary-card">
            <div className="mehraufwand-summary-title-row">
              <h2>Zeit</h2>
              <span>a 10 Min.</span>
            </div>
            <div className="mehraufwand-summary-controls">
              <label className="field">
                <select
                  value={timeSummaryFilter}
                  onChange={(event) => setTimeSummaryFilter(event.target.value)}
                >
                  <option value="all">Alle Dauer</option>
                  <option value="open">Nicht bearbeitete Dauer</option>
                  <option value="processed">Bearbeitete Dauer</option>
                </select>
              </label>
              <label className="field">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={timeRate}
                  onChange={(event) => setTimeRate(event.target.value)}
                />
              </label>
            </div>
            <strong>{timeSummary.minutes} Min. / {formatEuro(timeSummary.amount)}</strong>
          </div>
        </div>
        )}
      </div>

      <section className="card">
        <div className="section-head">
          <div>
            <h2>Mehraufwand erfassen</h2>
            <p className="section-text">
              Zeiten koennen nur in 10-Minuten-Schritten gespeichert werden.
            </p>
          </div>
          <div className="entry-summary">
            <span>{form.customer || "Kein Kunde"}</span>
            <span>{form.effortType || "Kein Aufwand"}</span>
          </div>
        </div>

        <div className="form-grid columns-2 entry-form-grid">
          <label className="field field-wide">
            <span>Kunde</span>
            <select
              value={form.customer}
              onChange={(event) => {
                setForm({ ...form, customer: event.target.value });
                setProcessStartedAt(event.target.value ? Date.now() : null);
              }}
            >
              <option value="">Bitte auswaehlen</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.name}>
                  {supplier.customerNumber
                    ? `${supplier.short} - ${supplier.customerNumber}`
                    : supplier.short}
                </option>
              ))}
            </select>
          </label>

          <label className="field field-wide">
            <span>Art des Aufwandes</span>
            <select
              value={form.effortType}
              onChange={(event) =>
                setForm({
                  ...form,
                  effortType: event.target.value,
                  customEffort: "",
                  expenseType:
                    event.target.value === "Auslagen" ? form.expenseType : "",
                  expenseAmount:
                    event.target.value === "Auslagen" ? form.expenseAmount : "",
                  displayCount:
                    event.target.value === "Displaybau" ? form.displayCount || 1 : 1,
                  displayUnitPrice:
                    event.target.value === "Displaybau" ? form.displayUnitPrice || 14 : 14,
                  damageReportCount:
                    event.target.value === "Schadensprotokoll"
                      ? form.damageReportCount || 1
                      : 1,
                  damageReportUnitPrice:
                    event.target.value === "Schadensprotokoll"
                      ? form.damageReportUnitPrice || 25
                      : 25,
                })
              }
            >
              <option value="">Bitte auswaehlen</option>
              {effortTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>

          {form.effortType === "Auslagen" && (
            <>
              <label className="field">
                <span>Auslagenart</span>
                <select
                  value={form.expenseType}
                  onChange={(event) =>
                    setForm({ ...form, expenseType: event.target.value })
                  }
                >
                  <option value="">Bitte auswaehlen</option>
                  {expenseTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>Wert in Euro</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.expenseAmount}
                  onChange={(event) =>
                    setForm({ ...form, expenseAmount: event.target.value })
                  }
                  placeholder="0,00"
                />
              </label>
            </>
          )}

          {form.effortType === "Displaybau" && (
            <>
              <label className="field">
                <span>Anzahl</span>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={form.displayCount}
                  onChange={(event) =>
                    setForm({ ...form, displayCount: event.target.value })
                  }
                />
              </label>
              <label className="field">
                <span>Wert je Display</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.displayUnitPrice}
                  onChange={(event) =>
                    setForm({ ...form, displayUnitPrice: event.target.value })
                  }
                />
              </label>
            </>
          )}

          {form.effortType === "Schadensprotokoll" && (
            <>
              <label className="field">
                <span>Anzahl</span>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={form.damageReportCount}
                  onChange={(event) =>
                    setForm({ ...form, damageReportCount: event.target.value })
                  }
                />
              </label>
              <label className="field">
                <span>Wert je Schadensprotokoll</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.damageReportUnitPrice}
                  onChange={(event) =>
                    setForm({ ...form, damageReportUnitPrice: event.target.value })
                  }
                />
              </label>
            </>
          )}

          {form.effortType && (
            <label className="field field-wide">
              <span>Kommentar</span>
              <textarea
                rows="3"
                placeholder="Optional beschreiben, was gemacht wurde"
                value={form.comment}
                onChange={(event) =>
                  setForm({ ...form, comment: event.target.value })
                }
              />
            </label>
          )}

          {form.effortType !== "Auslagen" && (
            <label className="field">
              <span>Zeit</span>
              <select
                value={form.durationMinutes}
                onChange={(event) =>
                  setForm({
                    ...form,
                    durationMinutes: Number(event.target.value),
                  })
                }
              >
                {durationOptions.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {minutes} Minuten
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="field">
            <span>Mitarbeiter</span>
            <input value={employeeName} readOnly />
          </label>

          <div className="field field-wide">
            <span>Foto</span>
            <div className="capture-card">
              <div>
                <strong>
                  {form.photoDataList.length > 0
                    ? `${form.photoDataList.length} Foto(s) vorhanden`
                    : "Kein Foto aufgenommen"}
                </strong>
                <p className="section-text">
                  Optionales Foto fuer den Mehraufwand aufnehmen.
                </p>
              </div>
              <div className="inline-actions">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="visually-hidden"
                  onChange={handleImageCapture}
                />
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="button button-secondary"
                >
                  Foto aufnehmen
                </button>
                {form.photoDataList.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm((currentForm) => {
                        const photoDataList = currentForm.photoDataList.slice(0, -1);

                        return {
                          ...currentForm,
                          photoDataList,
                          photoData: photoDataList[0] || "",
                        };
                      })
                    }
                    className="button button-inline-danger"
                  >
                    Letztes Foto entfernen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="button-row action-bar-sticky">
          <button onClick={handleSave} className="button button-primary">
            Mehraufwand speichern
          </button>
          <button
            onClick={() => setForm(initialForm)}
            className="button button-secondary"
          >
            Formular zuruecksetzen
          </button>
        </div>
      </section>

      <section className="card">
        <div className="section-head">
          <div>
            <h2>Gespeicherter Mehraufwand</h2>
            <p className="section-text">
              Filtere die Liste nach Kunde, Aufwand oder Mitarbeiter.
            </p>
          </div>
          <div className="inline-actions">
            <button
              type="button"
              onClick={() => setEffortsVisible((current) => !current)}
              className="button button-dark mde-only-button"
            >
              {effortsVisible ? "Liste ausblenden" : "Liste anzeigen"}
            </button>
            {user?.isAdmin && (
              <button onClick={handleCsvDownload} className="button button-primary">
                CSV herunterladen
              </button>
            )}
            <button onClick={resetFilters} className="button button-secondary">
              Filter zuruecksetzen
            </button>
          </div>
        </div>

        <div
          className={`mde-records-body ${
            effortsVisible ? "mde-records-expanded" : "mde-records-collapsed"
          }`}
        >
          <div className="form-grid columns-3">
            <label className="field">
              <span>Suche</span>
              <input
                placeholder="Kunde, Aufwand, Kommentar suchen"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>

            <label className="field">
              <span>Aufwand filtern</span>
              <select
                value={filterType}
                onChange={(event) => setFilterType(event.target.value)}
              >
                <option value="all">Alle Aufwaende</option>
                {effortTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Kunde filtern</span>
              <select
                value={filterCustomer}
                onChange={(event) => setFilterCustomer(event.target.value)}
              >
                <option value="all">Alle Kunden</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.name}>
                    {supplier.customerNumber
                      ? `${supplier.short} - ${supplier.customerNumber}`
                      : supplier.short}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Mitarbeiter filtern</span>
              <select
                value={filterEmployee}
                onChange={(event) => setFilterEmployee(event.target.value)}
              >
                <option value="all">Alle Mitarbeiter</option>
                {employees.map((employee) => (
                  <option key={employee} value={employee}>
                    {employee}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {filteredEfforts.length === 0 ? (
            <div className="empty-state">Keine passenden Eintraege gefunden.</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table mehraufwand-table">
                <thead>
                  <tr>
                    <th>Zeit</th>
                    <th>Kunde</th>
                    <th>Kundennummer</th>
                    <th>Aufwand</th>
                    <th>Auslagen</th>
                    <th>Auslagenart</th>
                    <th>Wert EUR</th>
                    <th>Beschädigte Pakete</th>
                    <th>Beschädigte Paletten</th>
                    <th>Kommentar</th>
                    <th>Dauer</th>
                    <th>Bearbeitungszeit</th>
                    <th>Mitarbeiter</th>
                    <th>Foto</th>
                    <th>Bearbeitet</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEfforts.map((effort) => (
                    <tr
                      key={effort.id}
                      className={effort.processed ? "row-processed" : ""}
                    >
                      <td>{effort.timestamp}</td>
                      <td>{effort.customer}</td>
                      <td>
                        {suppliersByName.get(effort.customer)?.customerNumber ||
                          "-"}
                      </td>
                      <td>
                        <ListTextPreview
                          value={
                            effort.effortType === "VAS sonstiges" &&
                            effort.customEffort
                              ? `${effort.effortType}: ${effort.customEffort}`
                              : effort.effortType
                          }
                        />
                      </td>
                      <td>{effort.effortType === "Auslagen" ? "Ja" : "Nein"}</td>
                      <td><ListTextPreview value={effort.expenseType} /></td>
                      <td>
                        <span className="inline-actions">
                          <span>{formatEuro(effort.expenseAmount)}</span>
                          <button
                            type="button"
                            onClick={() => handleEditExpenseAmount(effort)}
                            className="button button-inline-edit button-icon-edit"
                            aria-label="Wert bearbeiten"
                            title="Wert bearbeiten"
                          >
                            <EditIcon />
                          </button>
                        </span>
                      </td>
                      <td>{Number(effort.damagedPackages) || 0}</td>
                      <td>{Number(effort.damagedPallets) || 0}</td>
                      <td><ListTextPreview value={effort.comment} /></td>
                      <td>
                        <span className="inline-actions">
                          <span>
                            {Number(effort.durationMinutes) > 0
                              ? `${effort.durationMinutes} Minuten`
                              : "-"}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleEditDuration(effort)}
                            className="button button-inline-edit button-icon-edit"
                            aria-label="Dauer bearbeiten"
                            title="Dauer bearbeiten"
                          >
                            <EditIcon />
                          </button>
                        </span>
                      </td>
                      <td>{formatDuration(effort.processDurationSeconds)}</td>
                      <td>{effort.employee}</td>
                      <td>
                        {getStoredImages(effort, "photoDataList", "photoData").length > 0 ? (
                          <div className="media-actions">
                            {getStoredImages(effort, "photoDataList", "photoData").map(
                              (imageData, photoIndex) => (
                                <div
                                  key={`${effort.id}-photo-${photoIndex}`}
                                  className="media-actions"
                                >
                                  <button
                                    type="button"
                                    onClick={() => openStoredImage(imageData)}
                                    className="button button-inline-edit button-image-open"
                                    aria-label={`Bild ${photoIndex + 1} ansehen`}
                                    title={`Bild ${photoIndex + 1}`}
                                  >
                                    <CameraIcon />
                                    <span>{photoIndex + 1}</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      downloadStoredImage(
                                        imageData,
                                        getImageDownloadName(
                                          effort,
                                          imageData,
                                          photoIndex
                                        )
                                      )
                                    }
                                    className="button button-icon-download"
                                    aria-label={`Foto ${photoIndex + 1} herunterladen`}
                                  >
                                    ↓
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleToggleProcessed(effort)}
                          aria-label="Bearbeitet umschalten"
                          title="Bearbeitet"
                          className={`button button-inline-processed ${
                            effort.processed ? "button-inline-processed-active" : ""
                          }`}
                        >
                          <EditIcon />
                        </button>
                      </td>
                      <td>
                        {user?.isAdmin ? (
                          <button
                            onClick={() => handleDelete(effort.id)}
                            className="button button-inline-danger button-icon-danger"
                            aria-label="Loeschen"
                            title="Loeschen"
                          >
                            <TrashIcon />
                          </button>
                        ) : (
                          <span className="section-text">Keine Berechtigung</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
